/**
 * Client-safe display formatters (no server imports, no React).
 *
 * Single source of truth for exact money amounts (formatPaymentAmount: quote,
 * ticket, order and payout prices) and for date display (formatDateOnly,
 * formatDateTime, formatRelativeTime). Public "From $650" marketing prices
 * use formatPublicDressPrice in src/constants/pricing.ts instead.
 */

/** Formats an amount as currency with exactly two decimals (en-US locale). */
export function formatCurrency(
  amount: number,
  currencyCode: string = "USD"
): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Truncates text to maxLength characters, appending "..." when cut. */
export function truncateText(text: string, maxLength: number = 50): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
}

/**
 * Formats a payment amount in the given currency (defaults to USD), falling
 * back to a plain "$x.xx" string if the currency code is invalid.
 */
export function formatPaymentAmount(
  amount: number,
  currency?: string | null
): string {
  try {
    return formatCurrency(amount, currency || "USD");
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

const EMPTY_DATE = "\u2014";

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "Nov 4, 2026"; "—" for a missing or invalid date. */
export function formatDateOnly(dateString: string | null | undefined): string {
  const date = parseDate(dateString);
  if (!date) return EMPTY_DATE;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Nov 4, 2026, 3:05 PM"; "—" for a missing or invalid date. */
export function formatDateTime(dateString: string | null | undefined): string {
  const date = parseDate(dateString);
  if (!date) return EMPTY_DATE;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * "just now" / "5 minutes ago" / "3 days ago", then the formatDateOnly date
 * after a week; "—" for a missing or invalid date.
 */
export function formatRelativeTime(
  dateString: string | null | undefined
): string {
  const date = parseDate(dateString);
  if (!date) return EMPTY_DATE;
  const diffMs = Date.now() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  }

  return formatDateOnly(dateString);
}
